import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { guiaDeRuta } from '../utils/guias'
import { traduccionesDe, t as i18nT, tx } from '../utils/i18n'   // B-2259 · el botón se busca también por su texto traducido

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-172 · LA GUÍA DE LA PANTALLA: los globos de la primera vez (qué dicen: utils/guias.js)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  La monta Layout en todas las pantallas; sale solo si esta pantalla tiene guía y esta persona no la ha visto
//  (`preferencias.guias_vistas`, en su cuenta: viaja entre ordenadores). «Saltar la guía» o «Entendido» la dan por vista.
//  No sale en una visita del soporte ni en el modo doctor (Layout lo dice con `desactivada`).
//  Nada tapa la pantalla: el foco no recoge clics y el globo es lo único que se pulsa.
//  Lo medido en pantalla se divide por el zoom del «Tamaño de letra» (ver zoomDeLaPagina).

const zoomDeLaPagina = () => { try { return parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { return 1 } }

// ¿Lo ve quien está delante? Tener sitio no basta: un desplegable cerrado sigue en la página, escondido
// (visibility: hidden) pero con su caja, y el globo señalaría a la nada.
export function seVe(el) {
  try {
    if (!el || !el.getClientRects().length) return false
    if (typeof el.checkVisibility === 'function') return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
    const v = typeof getComputedStyle === 'function' ? getComputedStyle(el).visibility : 'visible'
    return v !== 'hidden' && v !== 'collapse'
  } catch (_) { return false }
}

// El elemento de un paso, en la página de verdad: por su selector o por el texto que enseña (el más corto que lo dice).
export function buscarAncla(p, doc = (typeof document !== 'undefined' ? document : null)) {
  if (!doc || !p) return null
  try {
    if (p.selector) {
      for (const el of doc.querySelectorAll(p.selector)) if (seVe(el)) return el
    }
    if (p.texto) {
      // Lo que dice el botón en castellano o, con la aplicación en otro idioma, su traducción (utils/i18n · traduccionesDe).
      const textos = [p.texto, ...traduccionesDe(p.texto)]
      let mejor = null
      for (const el of doc.querySelectorAll(p.etiqueta || 'button')) {
        const t = String(el.textContent || '').replace(/\s+/g, ' ').trim()
        if (!seVe(el)) continue
        if (textos.includes(t)) return el
        for (const x of textos) {
          if (t.startsWith(x) && t.length <= x.length + 8 && (!mejor || t.length < mejor.t.length)) mejor = { el, t }
        }
      }
      if (mejor) return mejor.el
    }
  } catch (_) { /* un selector raro no rompe la pantalla */ }
  return null
}

export default function GuiaDePantalla({ ruta, preferencias, desactivada = false, buscar = buscarAncla }) {
  const qc = useQueryClient()
  const guia = guiaDeRuta(ruta)
  const vistas = (preferencias && preferencias.guias_vistas && typeof preferencias.guias_vistas === 'object') ? preferencias.guias_vistas : {}
  const toca = !!guia && !desactivada && !!preferencias && !vistas[guia.clave]
  const [pasos, setPasos] = useState(null)      // los pasos cuya ancla está en la página
  const [i, setI] = useState(0)
  const [caja, setCaja] = useState(null)
  const [cerrada, setCerrada] = useState(null)  // la guía que se ha cerrado en esta visita

  // La pantalla puede tardar en pintarse: se busca unas cuantas veces, y el paso que no aparece se salta.
  useEffect(() => {
    setPasos(null); setI(0)
    if (!toca) return undefined
    let vivo = true, intentos = 0, t = null
    const mirar = () => {
      if (!vivo) return
      const hay = guia.pasos.filter(p => buscar(p))
      if (hay.length || intentos >= 5) { setPasos(hay); return }
      intentos++
      t = setTimeout(mirar, 700)
    }
    t = setTimeout(mirar, 900)
    return () => { vivo = false; clearTimeout(t) }
  }, [guia ? guia.clave : null, toca]) // eslint-disable-line react-hooks/exhaustive-deps

  const paso = pasos && pasos[i]
  // Dónde está el paso de ahora, y otra vez si la página se mueve.
  useEffect(() => {
    if (!paso) { setCaja(null); return undefined }
    const medir = () => {
      const el = buscar(paso)
      if (!el) { setCaja(null); return }
      const r = el.getBoundingClientRect()
      const z = zoomDeLaPagina()
      setCaja({ x: r.left / z, y: r.top / z, w: r.width / z, h: r.height / z })
    }
    medir()
    if (typeof window === 'undefined') return undefined
    window.addEventListener('resize', medir)
    window.addEventListener('scroll', medir, true)
    return () => { window.removeEventListener('resize', medir); window.removeEventListener('scroll', medir, true) }
  }, [paso]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!toca || !guia || cerrada === guia.clave || !pasos || !pasos.length || !paso) return null

  const terminar = () => {
    setCerrada(guia.clave)
    const nuevas = { ...vistas, [guia.clave]: true }
    api.patch('/auth/preferencias', { guias_vistas: nuevas })
      .then(r => { try { qc.setQueryData(['auth-preferencias'], r.data) } catch (_) { /* sin caché */ } })
      .catch(() => { /* si no se guarda, vuelve a salir otro día: no pasa nada */ })
  }
  const ultimo = i === pasos.length - 1
  // El globo debajo del elemento si cabe; si no, encima. Pegado al borde izquierdo del elemento, sin salirse.
  const z = zoomDeLaPagina()
  const alto = typeof window !== 'undefined' ? window.innerHeight / z : 800
  const ancho = typeof window !== 'undefined' ? window.innerWidth / z : 1200
  const pos = caja
    ? ((caja.y + caja.h + 220 < alto) ? { top: caja.y + caja.h + 14 } : { bottom: Math.max(12, alto - caja.y + 14) })
    : { bottom: 24 }
  const izquierda = caja ? Math.max(12, Math.min(caja.x, ancho - 344)) : Math.max(12, (ancho - 320) / 2)

  return (
    <div data-guia={guia.clave}>
      {caja && (
        <div aria-hidden="true" style={{ position: 'fixed', left: caja.x - 6, top: caja.y - 6, width: caja.w + 12, height: caja.h + 12,
          borderRadius: '10px', boxShadow: '0 0 0 3px var(--primary), 0 0 0 9999px rgba(15, 23, 42, 0.32)', pointerEvents: 'none', zIndex: 1400 }} />
      )}
      <div role="dialog" aria-live="polite" aria-label={paso.titulo} data-guia-globo={i + 1}
        style={{ position: 'fixed', ...pos, left: izquierda, width: 'min(320px, calc((100vw / var(--z, 1)) - 24px))', background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)', padding: '14px 16px', zIndex: 1401 }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{tx('components.GuiaDePantalla.paso_de', 'Paso {{x}} de {{length}}', { x: i + 1, length: pasos.length })}</div>
        <div style={{ fontSize: '15px', fontWeight: 800, margin: '4px 0', color: 'var(--text-primary)' }}>{paso.titulo}</div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{paso.cuerpo}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
          <button type="button" data-guia-saltar onClick={terminar}
            style={{ background: 'none', border: 'none', padding: '6px 0', color: 'var(--text-muted)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>{i18nT('components.GuiaDePantalla.saltar_la_guia', 'Saltar la guía')}</button>
          <button type="button" data-guia-siguiente onClick={() => (ultimo ? terminar() : setI(i + 1))}
            style={{ background: 'var(--primary)', color: 'var(--sobre-primary)', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
            {ultimo ? i18nT('components.GuiaDePantalla.entendido', 'Entendido') : i18nT('components.GuiaDePantalla.siguiente', 'Siguiente')}
          </button>
        </div>
      </div>
    </div>
  )
}
